import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ApolloError } from '@apollo/client'
import { Modal, Button, FormGroup, FormLabel, FormControl, Form, InputGroup } from 'react-bootstrap'
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import ColorPicker from 'Shared/form/colorpicker'
import IconPickerSelect from 'Shared/form/iconPickerSelect'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocumentBasePath } from 'cg/components/documents/useDocumentBasePath'

const ValidationSchema = () =>
  Yup.object().shape({
    title: Yup.string().required('Required')
  })

const CheckBox = props => <Form.Check {...props} />

const CreateOrUpdateSectionModal = ({ section, sectionId, mutation, callback, addNestedId, toggleGroupOpen, numTabs = 0 }) => {
  const navigate = useNavigate()
  const { id, itemType } = section
  const { versionId } = useParams()
  const documentBasePath = useDocumentBasePath()
  const [shouldRemoveIcon, setShouldRemoveIcon] = useState(false)
  const isCreating = id == null
  let itemTypeLabel = 'Section'
  if (itemType === 'group') {
    itemTypeLabel = 'Group'
  } else if (itemType === 'tab') {
    itemTypeLabel = 'Tab'
  }

  const formDefaultValues = { ...section }
  if (section.icon == null) {
    formDefaultValues.icon = ''
  }
  if (section.color == null) {
    formDefaultValues.color = ''
  }
  if (section.title == null) {
    formDefaultValues.title = ''
  }
  if (section.tabId == null) {
    formDefaultValues.tabId = null
  }

  const handleMutationCompleted = data => {
    const { createDocumentSection, updateDocumentSection } = data
    let id = 0
    let firstSectionId = null
    if (createDocumentSection) {
      id = createDocumentSection.id
      if (createDocumentSection.firstSection) {
        firstSectionId = createDocumentSection.firstSection.id
      }
    } else if (updateDocumentSection) {
      id = updateDocumentSection.id
      if (updateDocumentSection.firstSection) {
        firstSectionId = updateDocumentSection.firstSection.id
      }
    }
    if (firstSectionId != null) {
      id = firstSectionId
      if (numTabs === 0 && createDocumentSection != null && itemType === 'tab') {
        // creating the first tab... ensure the tab is "selected"
        Promise.resolve().then(() => {
          navigate(`${documentBasePath}/editor/${versionId}`)
        })
      }
    }
    Promise.resolve().then(() => {
      navigate(`${documentBasePath}/editor/${versionId}/${id}`)
    })
  }

  return (
    <Modal
      show
      onHide={() => {
        callback(false)
      }}
    >
      <Modal.Header>
        <Modal.Title>
          {isCreating ? 'Create' : 'Edit'} <span>{itemTypeLabel}</span>
        </Modal.Title>
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={formDefaultValues}
        onSubmit={values => {
          // don't pass empty string values to backend for icon or color
          if (values.icon === '') {
            values.icon = null
          }
          if (values.color === '') {
            values.color = null
          }
          if (shouldRemoveIcon) {
            // Set both icon and color to null if removing icon
            values.icon = null
            values.color = null
          }
          callback(false)
          mutation.mutateOptimistic({ variables: { ...values }, onCompleted: handleMutationCompleted })
        }}
        validationSchema={ValidationSchema}
      >
        {({ values, errors, isSubmitting, dirty, isValid, setFieldValue }) => (
          <FormikForm>
            <Modal.Body>
              <FormGroup className="mb-3">
                <FormLabel htmlFor="title">Title</FormLabel>
                <InputGroup>
                  <Field
                    name="icon"
                    as={IconPickerSelect}
                    inModal
                    position="left"
                    allowRemove
                    iconName={values.icon !== '' ? values.icon : ''}
                    onSelect={icon => {
                      if (icon === '') {
                        setFieldValue('icon', 'far fa-circle-plus')
                        setShouldRemoveIcon(true)
                      } else {
                        setShouldRemoveIcon(false)
                        setFieldValue('icon', icon)
                      }
                    }}
                  />
                  <Field name="color" as={ColorPicker} inModal />
                  <Field name="title" as={FormControl} placeholder="Title" isInvalid={!!errors.title} />
                </InputGroup>
                <ErrorMessage name="title" render={msg => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>} />
              </FormGroup>
              {itemType === 'section' && (
                <FormGroup className="mb-3">
                  <Field
                    name="excludeFromPublish"
                    checked={values.excludeFromPublish || false}
                    as={CheckBox}
                    label="Exclude section from published version"
                    onChange={() => setFieldValue('excludeFromPublish', !values.excludeFromPublish)}
                  />
                  <ErrorMessage
                    name="excludeFromPublish"
                    render={msg => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                  />
                </FormGroup>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="submit"
                variant={!dirty || !errors || !isValid ? 'light' : 'primary'}
                disabled={(isSubmitting && !mutation.error) || !dirty || !isValid || !errors}
              >
                {isCreating ? 'Create' : 'Update'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  callback(false)
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  )
}

CreateOrUpdateSectionModal.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string,
    nextItemId: PropTypes.string,
    title: PropTypes.string,
    itemType: PropTypes.oneOf(['group', 'section', 'tab']).isRequired,
    documentVersionId: PropTypes.string.isRequired,
    groupId: PropTypes.string,
    tabId: PropTypes.string,
    path: PropTypes.string,
    excludeFromPublish: PropTypes.bool,
    color: PropTypes.string,
    icon: PropTypes.string
  }),
  sectionId: PropTypes.string,
  mutation: PropTypes.shape({
    mutate: PropTypes.func,
    mutateOptimistic: PropTypes.func,
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(ApolloError)
  }),
  callback: PropTypes.func,
  addNestedId: PropTypes.func,
  toggleGroupOpen: PropTypes.bool,
  numTabs: PropTypes.number
}

export default CreateOrUpdateSectionModal
